import D3 from 'd3'
import moment from 'moment'
import { LINE_GAP } from 'components/features/external/PublicSpendingTrend/constants'
import { PublicTrendItem } from 'interfaces/publicDashboard'

export type Point = [number, number]

type Cords = {
  x: number
  y: number
}

export const drawArea = (points: [number, number][], minY: number) => {
  return D3.svg.area()
    .interpolate('linear')
    .y0(minY)(points)
}

const abscissa = (x: number, minX: number, ratioX: number) => {
  return (x - minX) * ratioX
}

const ordinate = (y: number, minY: number, maxY: number, ratioY: number) => {
  const invertedOrdinate = maxY - (y - minY)
  const normOrdinate = invertedOrdinate - minY
  return normOrdinate * ratioY
}

export const drawLine = D3.svg.line()
  .interpolate('linear')


const calcEdges = (points: Cords[]) => {
  const edges = points.reduce((acc, item) => {
    return {
      maxX: Math.max(acc.maxX, item.x),
      minX: Math.min(acc.minX, item.x),
      maxY: Math.max(acc.maxY, item.y),
      minY: Math.min(acc.minY, item.y),
    }
  }, {
    minX: points[0].x,
    maxX: points[0].x,
    minY: points[0].y,
    maxY: points[0].y,
  })

  return {
    ...edges,
    deltaX: edges.maxX - edges.minX,
    deltaY: edges.maxY - edges.minY,
  }
}

type CalcChartProps = {
  noGap?: boolean
}
export const calcChart = (rawPoints: PublicTrendItem[], clientWidth: number, clientHeight: number, options: CalcChartProps = {}) => {
  const halfOfHeight = clientHeight / 2
  const cords = rawPoints.map((item) => ({
    y: item.amount,
    x: moment(item.date).valueOf(),
  }))

  const edges = calcEdges(cords)

  let zero = 0
  // adjust offsets
  if (edges.maxY > 0 && edges.minY >= 0) {
    if (!options.noGap) {
      edges.minY -= edges.deltaY * LINE_GAP
    }
    zero = edges.minY
  }
  if (edges.maxY <= 0 && edges.minY < 0) {
    if (!options.noGap) {
      edges.maxY += edges.deltaY * LINE_GAP
    }
    zero = edges.maxY
  }
  edges.deltaY = edges.maxY - edges.minY

  const ratio = {
    x: clientWidth / edges.deltaX,
    y: (clientHeight / edges.deltaY),
  }

  const points = cords.map((point) => {
    let nextOrdinate = ordinate(point.y, edges.minY, edges.maxY, ratio.y)
    if (isNaN(nextOrdinate)) {
      nextOrdinate = halfOfHeight
    }
    return [
      abscissa(point.x, edges.minX, ratio.x),
      nextOrdinate,
    ] as [number, number]
  })

  let zeroAbscissa = ordinate(zero, edges.minY, edges.maxY, ratio.y)
  if (isNaN(zeroAbscissa)) {
    zeroAbscissa = halfOfHeight
  }
  return {
    points,
    zeroAbscissa,
  }
}
